import React, { useState, Component } from 'react';
import { ArrowUpIcon, ArrowDownIcon, TrendingUpIcon, PiggyBankIcon, TrendingDownIcon, BarChartIcon, LineChartIcon, SendIcon, CreditCardIcon, UsersIcon, TagIcon, PlusIcon, CheckCircleIcon, XIcon, CoinsIcon, PercentIcon, PlusCircleIcon, InfoIcon, CopyIcon, ShareIcon, QrCodeIcon, CheckIcon, SmartphoneIcon, SearchIcon, AtSignIcon, CameraIcon, ArrowRightIcon, EyeIcon, EyeOffIcon, ScanFaceIcon, ShieldCheckIcon, WalletIcon, CalendarIcon, DollarSignIcon, ActivityIcon, GripVerticalIcon, SettingsIcon, MoveVerticalIcon, UmbrellaIcon, HomeIcon, PlaneIcon } from 'lucide-react';
import { Mood, MoodType } from './Mood';
import { SavingsTracker } from './SavingsTracker';
import { SendMoney } from './SendMoney';
import { ReceiveMoney } from './ReceiveMoney';
interface MonthlyPayment {
  id: string;
  name: string;
  amount: number;
  dueDate: string;
  status: 'paid' | 'pending' | 'overdue';
  category: string;
  recurring: boolean;
}
export function AccountSummary({
  monthlyPayments,
  onCardShipped
}: {
  monthlyPayments: MonthlyPayment[];
  onCardShipped?: () => void;
}) {
  const [showCardRequest, setShowCardRequest] = useState(false);
  const [cardRequested, setCardRequested] = useState(false);
  const [hideCardSection, setHideCardSection] = useState(false);
  const [selectedMood, setSelectedMood] = useState<MoodType | null>(null);
  const [showMoodNotification, setShowMoodNotification] = useState(false);
  const [showMoodComponent, setShowMoodComponent] = useState(true);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [donationPercentage, setDonationPercentage] = useState(50);
  const [donationComplete, setDonationComplete] = useState(false);
  const [changeCollectionAmount, setChangeCollectionAmount] = useState(18.75);
  const [currentBalance, setCurrentBalance] = useState(4258.0);
  const [fundsReturned, setFundsReturned] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [sendAmount, setSendAmount] = useState('');
  const [sendRecipient, setSendRecipient] = useState('');
  const [sendComplete, setSendComplete] = useState(false);
  const moodEmojis: Record<MoodType, string> = {
    great: '😄',
    good: '🙂',
    okay: '😐',
    bad: '😟',
    awful: '😢'
  };
  const moodLabels: Record<MoodType, string> = {
    great: 'Great',
    good: 'Good',
    okay: 'Okay',
    bad: 'Bad',
    awful: 'Awful'
  };
  const handleMoodSelect = (mood: MoodType) => {
    setSelectedMood(mood);
    setShowMoodComponent(false);
    setShowMoodNotification(true);
    setTimeout(() => {
      setShowMoodNotification(false);
    }, 5000);
  };
  const handleRequestCard = () => {
    setCardRequested(true);
    setTimeout(() => {
      setShowCardRequest(false);
      setHideCardSection(true);
      setTimeout(() => {
        if (onCardShipped) {
          onCardShipped();
        }
      }, 120000);
    }, 3000);
  };
  const handleDonateClick = () => {
    setShowDonateModal(true);
    setDonationComplete(false);
  };
  const handleDonationComplete = () => {
    const donationAmount = changeCollectionAmount * donationPercentage / 100;
    setChangeCollectionAmount(prevAmount => {
      const newAmount = Math.max(0, prevAmount - donationAmount);
      return Number(newAmount.toFixed(2));
    });
    setFundsReturned(false);
    setDonationComplete(true);
    setTimeout(() => {
      setShowDonateModal(false);
      setDonationComplete(false);
    }, 3000);
  };
  const handleReturnFunds = () => {
    const returnAmount = changeCollectionAmount * donationPercentage / 100;
    setCurrentBalance(prevBalance => {
      const newBalance = prevBalance + returnAmount;
      return Number(newBalance.toFixed(2));
    });
    setChangeCollectionAmount(prevAmount => {
      const newAmount = Math.max(0, prevAmount - returnAmount);
      return Number(newAmount.toFixed(2));
    });
    setFundsReturned(true);
    setDonationComplete(true);
    setTimeout(() => {
      setShowDonateModal(false);
      setDonationComplete(false);
      setFundsReturned(false);
    }, 3000);
  };
  const handleSendClick = () => {
    setShowSendModal(true);
    setSendComplete(false);
    setSendAmount('');
    setSendRecipient('');
  };
  const handleReceiveClick = () => {
    setShowReceiveModal(true);
  };
  const handleSendMoney = () => {
    if (!sendAmount || !sendRecipient) return;
    const amount = parseFloat(sendAmount);
    if (isNaN(amount) || amount <= 0 || amount > currentBalance) return;
    setCurrentBalance(prev => prev - amount);
    setSendComplete(true);
    setTimeout(() => {
      setShowSendModal(false);
      setSendComplete(false);
      setSendAmount('');
      setSendRecipient('');
    }, 2000);
  };
  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'Send':
        handleSendClick();
        break;
      case 'Receive':
        handleReceiveClick();
        break;
      case 'Income':
        // Navigate to income page or show income modal
        console.log('Income action');
        break;
      case 'Pay Bills':
        // Navigate to bills or show bills modal
        console.log('Pay Bills action');
        break;
      case 'Split':
        // Show split payment modal
        console.log('Split action');
        break;
      case 'More':
        // Show more actions menu
        console.log('More actions');
        break;
    }
  };
  const donationAmount = changeCollectionAmount * donationPercentage / 100;
  const giftAidAmount = donationAmount * 0.25;
  const totalImpact = donationAmount + giftAidAmount;
  const actions = [{
    icon: SendIcon,
    label: 'Send',
    color: 'bg-blue-100 text-blue-600'
  }, {
    icon: ArrowDownIcon,
    label: 'Receive',
    color: 'bg-green-100 text-green-600'
  }, {
    icon: WalletIcon,
    label: 'Income',
    color: 'bg-purple-100 text-purple-600'
  }, {
    icon: TagIcon,
    label: 'Pay Bills',
    color: 'bg-yellow-100 text-yellow-600'
  }, {
    icon: UsersIcon,
    label: 'Split',
    color: 'bg-pink-100 text-pink-600'
  }, {
    icon: PlusIcon,
    label: 'More',
    color: 'bg-gray-100 text-gray-600'
  }];
  // Savings accounts data for the tracker
  const savingsAccounts = [{
    id: 'emergency',
    name: 'Emergency Fund',
    priority: 1,
    current: 4500,
    target: 5000,
    interest: 45,
    icon: UmbrellaIcon,
    color: 'text-primary',
    bgColor: 'bg-primary'
  }, {
    id: 'house',
    name: 'House Deposit',
    priority: 2,
    current: 12000,
    target: 25000,
    interest: 125,
    icon: HomeIcon,
    color: 'text-secondary',
    bgColor: 'bg-secondary'
  }, {
    id: 'vacation',
    name: 'Vacation Fund',
    priority: 3,
    current: 2800,
    target: 8000,
    interest: 28,
    icon: PlaneIcon,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500'
  }];
  const savingsFlows = [{
    accountId: 'emergency',
    received: 250,
    overflow: 0
  }, {
    accountId: 'house',
    received: 400,
    overflow: 0
  }, {
    accountId: 'vacation',
    received: 150,
    overflow: 0
  }];
  return <>
      {showMoodNotification && selectedMood && <div className="fixed top-16 left-0 right-0 z-50 animate-slide-down" style={{
      animation: 'slideDown 0.3s ease-out'
    }} data-id="element-56">
          <div className="mx-4 mt-2" data-id="element-57">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 max-w-2xl mx-auto" data-id="element-58">
              <div className="flex items-center justify-between" data-id="element-59">
                <div className="flex items-center" data-id="element-60">
                  <div className="text-4xl mr-4" data-id="element-61">
                    {moodEmojis[selectedMood]}
                  </div>
                  <div data-id="element-62">
                    <h4 className="font-semibold text-text-primary text-lg" data-id="element-63">
                      Thanks for sharing!
                    </h4>
                    <p className="text-sm text-text-secondary mt-0.5" data-id="element-64">
                      You're feeling {moodLabels[selectedMood].toLowerCase()}{' '}
                      about your finances today
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowMoodNotification(false)} className="ml-4 p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" data-id="element-65">
                  <XIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" data-id="element-66" />
                </button>
              </div>
            </div>
          </div>
        </div>}

      <style data-id="element-67">{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-68">
        <div className="bg-card rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-69">
          <div className="grid grid-cols-2 gap-4" data-id="element-70">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 h-full" data-id="element-71">
              <div className="flex items-center mb-2" data-id="element-72">
                <WalletIcon className="w-4 h-4 text-primary mr-1" data-id="element-73" />
                <p className="text-xs font-medium text-text-secondary" data-id="element-74">
                  Current Balance
                </p>
              </div>
              <div className="flex items-center justify-between" data-id="element-75">
                <p className="text-lg font-bold text-text-primary" data-id="element-76">
                  £{currentBalance.toFixed(2)}
                </p>
              </div>
            </div>
            <div onClick={() => changeCollectionAmount > 0 && handleDonateClick()} className={`bg-gray-50 rounded-lg p-3 border border-gray-100 h-full transition-colors ${changeCollectionAmount > 0 ? 'hover:bg-gray-100 cursor-pointer hover:border-primary/20' : 'cursor-not-allowed'}`} data-id="element-77">
              <div className="flex items-center mb-2" data-id="element-78">
                <CoinsIcon className="w-4 h-4 text-primary mr-1" data-id="element-79" />
                <p className="text-xs font-medium text-text-secondary" data-id="element-80">
                  Change Pot
                </p>
              </div>
              <div className="flex items-center justify-between" data-id="element-81">
                <p className="text-lg font-bold text-text-primary" data-id="element-82">
                  £{changeCollectionAmount.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {showDonateModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-83">
              <div className="bg-white rounded-xl p-5 max-w-md w-full mx-4" data-id="element-84">
                {donationComplete ? <div className="flex flex-col items-center text-center py-4" data-id="element-85">
                    <div className={`w-12 h-12 ${fundsReturned ? 'bg-blue-100' : 'bg-green-100'} rounded-full flex items-center justify-center mb-3`} data-id="element-86">
                      <CheckCircleIcon className={`w-7 h-7 ${fundsReturned ? 'text-blue-600' : 'text-green-600'}`} data-id="element-87" />
                    </div>
                    <p className="text-lg font-bold text-text-primary" data-id="element-88">
                      {fundsReturned ? 'Funds Returned!' : 'Thank You!'}
                    </p>
                    <p className="text-sm text-text-secondary mt-1 mb-2" data-id="element-89">
                      {fundsReturned ? `£${donationAmount.toFixed(2)} has been added to your current balance` : `You've donated £${donationAmount.toFixed(2)} to charity`}
                    </p>
                    {!fundsReturned && <div className="bg-primary/5 p-3 rounded-lg mb-4 w-full" data-id="element-90">
                        <p className="text-sm text-text-secondary mb-1" data-id="element-91">
                          With GiftAid, your donation has a total impact of:
                        </p>
                        <p className="text-lg font-bold text-primary" data-id="element-92">
                          £{totalImpact.toFixed(2)}
                        </p>
                      </div>}
                    <p className="text-xs text-text-secondary" data-id="element-93">
                      {fundsReturned ? 'Your balance has been updated' : 'Your donation will make a difference'}
                    </p>
                  </div> : <>
                    <div className="flex justify-between items-start mb-4" data-id="element-94">
                      <div data-id="element-95">
                        <h3 className="text-lg font-bold text-text-primary" data-id="element-96">
                          Donate Your Change
                        </h3>
                        <p className="text-sm text-text-secondary mt-1" data-id="element-97">
                          Choose how much of your £
                          {changeCollectionAmount.toFixed(2)} change collection
                          to donate
                        </p>
                      </div>
                      <button onClick={() => setShowDonateModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-98">
                        <XIcon className="w-5 h-5" data-id="element-99" />
                      </button>
                    </div>
                    <div className="my-6" data-id="element-100">
                      <div className="flex justify-between mb-2" data-id="element-101">
                        <span className="text-sm text-text-secondary" data-id="element-102">
                          Donation Percentage
                        </span>
                        <span className="text-sm font-medium text-primary" data-id="element-103">
                          {donationPercentage}%
                        </span>
                      </div>
                      <input type="range" min="0" max="100" step="5" value={donationPercentage} onChange={e => setDonationPercentage(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary" data-id="element-104" />
                      <div className="flex justify-between mt-2 text-xs text-text-secondary" data-id="element-105">
                        <span data-id="element-106">0%</span>
                        <span data-id="element-107">50%</span>
                        <span data-id="element-108">100%</span>
                      </div>
                    </div>
                    <div className="space-y-3 mb-4" data-id="element-109">
                      <div className="bg-gray-50 p-3 rounded-lg" data-id="element-110">
                        <div className="flex justify-between items-center" data-id="element-111">
                          <div className="flex items-center" data-id="element-112">
                            <PercentIcon className="w-4 h-4 text-primary mr-2" data-id="element-113" />
                            <span className="text-sm text-text-primary" data-id="element-114">
                              You will donate
                            </span>
                          </div>
                          <span className="text-sm font-bold text-text-primary" data-id="element-115">
                            £{donationAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="bg-primary/5 p-3 rounded-lg" data-id="element-116">
                        <div className="flex justify-between items-center mb-1" data-id="element-117">
                          <div className="flex items-center" data-id="element-118">
                            <PlusCircleIcon className="w-4 h-4 text-secondary mr-2" data-id="element-119" />
                            <span className="text-sm text-text-primary" data-id="element-120">
                              GiftAid adds
                            </span>
                          </div>
                          <span className="text-sm font-bold text-secondary" data-id="element-121">
                            £{giftAidAmount.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-start mt-1" data-id="element-122">
                          <InfoIcon className="w-3 h-3 text-text-secondary mt-0.5 mr-1 flex-shrink-0" data-id="element-123" />
                          <p className="text-xs text-text-secondary" data-id="element-124">
                            GiftAid adds 25% to your donation at no extra cost
                            to you
                          </p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-3 rounded-lg" data-id="element-125">
                        <div className="flex justify-between items-center" data-id="element-126">
                          <span className="text-sm font-medium text-text-primary" data-id="element-127">
                            Total Impact
                          </span>
                          <span className="text-sm font-bold text-primary" data-id="element-128">
                            £{totalImpact.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg" data-id="element-129">
                        <div className="flex justify-between items-center" data-id="element-130">
                          <div className="flex items-center" data-id="element-131">
                            <CoinsIcon className="w-4 h-4 text-text-secondary mr-2" data-id="element-132" />
                            <span className="text-sm text-text-primary" data-id="element-133">
                              Remaining in collection
                            </span>
                          </div>
                          <span className="text-sm font-bold text-text-primary" data-id="element-134">
                            £
                            {(changeCollectionAmount - donationAmount).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-2" data-id="element-135">
                      <button onClick={() => setShowDonateModal(false)} className="w-full px-4 py-2 text-sm font-medium text-text-primary bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-136">
                        Cancel
                      </button>
                      <div className="flex space-x-2" data-id="element-137">
                        <button onClick={handleReturnFunds} className="flex-1 px-4 py-2 text-sm font-medium text-primary bg-primary/10 border border-primary/20 rounded-lg hover:bg-primary/20" data-id="element-138">
                          Return Funds
                        </button>
                        <button onClick={handleDonationComplete} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90" data-id="element-139">
                          Confirm Donation
                        </button>
                      </div>
                    </div>
                  </>}
              </div>
            </div>}

          {!hideCardSection && <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-140">
              <div className="flex justify-between items-center mb-3" data-id="element-141">
                <p className="text-sm font-medium text-text-secondary" data-id="element-142">
                  Request your University of Cambridge Card Now
                </p>
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full" data-id="element-143">
                  New
                </span>
              </div>
              {showCardRequest ? <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-144">
                  {cardRequested ? <div className="flex flex-col items-center text-center" data-id="element-145">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-2" data-id="element-146">
                        <CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-147" />
                      </div>
                      <p className="text-sm font-medium text-text-primary" data-id="element-148">
                        Card request submitted!
                      </p>
                      <p className="text-xs text-text-secondary mt-1" data-id="element-149">
                        Your University of Cambridge card will arrive in 3-5
                        business days
                      </p>
                    </div> : <div data-id="element-150">
                      <div className="flex justify-between items-start mb-3" data-id="element-151">
                        <div data-id="element-152">
                          <h4 className="text-sm font-bold text-text-primary" data-id="element-153">
                            Confirm Card Request
                          </h4>
                          <p className="text-xs text-text-secondary mt-1" data-id="element-154">
                            Request your University of Cambridge exclusive debit
                            card
                          </p>
                        </div>
                        <button onClick={() => setShowCardRequest(false)} className="text-gray-400 hover:text-gray-600" data-id="element-155">
                          <XIcon className="w-4 h-4" data-id="element-156" />
                        </button>
                      </div>
                      <div className="flex justify-end space-x-2 mt-3" data-id="element-157">
                        <button onClick={() => setShowCardRequest(false)} className="px-3 py-1.5 text-xs font-medium text-text-primary bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-158">
                          Cancel
                        </button>
                        <button onClick={handleRequestCard} className="px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary/90" data-id="element-159">
                          Confirm Request
                        </button>
                      </div>
                    </div>}
                </div> : <button onClick={() => setShowCardRequest(true)} className="w-full group relative overflow-hidden rounded-lg shadow-sm hover:shadow-md transition-all duration-300" data-id="element-160">
                  <div className="relative w-full h-auto bg-white overflow-hidden rounded-lg" data-id="element-161">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cqsXrRfNthMHdJAUhheviM/53697a95-dadf-4e56-bfca-d3336b4e2b78-removebg-preview.png" alt="University of Cambridge Debit Card" className="w-full h-auto object-contain" data-id="element-162" />
                  </div>
                  <div className="p-3 bg-white" data-id="element-163">
                    <div className="flex justify-between items-center" data-id="element-164">
                      <div data-id="element-165">
                        <p className="text-sm font-medium text-text-primary" data-id="element-166">
                          University of Cambridge Card
                        </p>
                        <p className="text-xs text-text-secondary" data-id="element-167">
                          Exclusive student banking benefits
                        </p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full" data-id="element-168">
                        Exclusive
                      </span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100" data-id="element-169">
                    <div className="bg-white px-3 py-1.5 rounded-full text-xs font-medium text-primary" data-id="element-170">
                      Request Now
                    </div>
                  </div>
                </button>}
            </div>}

          <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-171">
            <p className="text-sm font-medium text-text-secondary mb-3" data-id="element-172">
              Quick Actions
            </p>
            <div className="grid grid-cols-3 gap-2" data-id="element-173">
              {actions.map((action, index) => <button key={index} onClick={() => handleQuickAction(action.label)} className="flex flex-col items-center justify-center p-2 bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-174">
                  <div className={`w-8 h-8 rounded-full ${action.color} flex items-center justify-center mb-1`} data-id="element-175">
                    <action.icon className="w-4 h-4" data-id="element-176" />
                  </div>
                  <span className="text-xs font-medium text-text-primary" data-id="element-177">
                    {action.label}
                  </span>
                </button>)}
            </div>
          </div>
        </div>

        {showMoodComponent && <div className="bg-card rounded-xl shadow-sm border border-gray-100 md:col-span-2" data-id="element-178">
            <Mood data-id="account-mood" title="How are you feeling about your finances today?" subtitle="Your feedback helps us provide better insights" onMoodSelect={handleMoodSelect} />
          </div>}

        {/* Smart Savings Tracker */}
        <div className="md:col-span-2" data-id="element-179">
          <SavingsTracker accounts={savingsAccounts} flows={savingsFlows} data-id="smart-savings-tracker" />
        </div>

        <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-5 shadow-sm text-white" data-id="element-180">
          <div className="flex justify-between items-start mb-2" data-id="element-181">
            <p className="text-sm opacity-90" data-id="element-182">Rewards Points</p>
            <span className="text-xs px-2 py-1 bg-white/20 rounded-full" data-id="element-183">
              Platinum
            </span>
          </div>
          <h3 className="text-2xl font-bold" data-id="element-184">2,450 pts</h3>
          <p className="text-xs opacity-80 mt-1" data-id="element-185">+180 pts this month</p>
          <button className="w-full bg-white/20 hover:bg-white/30 py-2 px-3 rounded-lg text-sm font-medium mt-4" data-id="element-186">
            Redeem Rewards
          </button>
        </div>
      </div>

      {/* Send Money Modal */}
      {showSendModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-187">
          <SendMoney data-id="account-summary-send-money" onClose={() => setShowSendModal(false)} />
        </div>}

      {/* Receive Money Modal */}
      {showReceiveModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-188">
          <ReceiveMoney data-id="account-summary-receive-money" moneyTag="@johndoe" paymentLink="https://pay.cauri.com/to/johndoe" accountName="John Doe" accountNumber="12345678" sortCode="12-34-56" onClose={() => setShowReceiveModal(false)} />
        </div>}
    </>;
}